(() => {
  const WIDGET_SELECTOR =
    '.jdgm-rev-widg:not([data-cd-jdgm-pagination-snapshot="true"])';
  const JUDGEME_SCOPE_SELECTOR =
    ".jdgm-legacy-widget-content, .jdgm-widget, .jdgm-review-widget, .jdgm-rev-widg";

  let pending = false;
  let domObserver = null;
  let paginationLoad = null;
  let stablePaginationHeight = 0;
  let stablePaginationWidth = 0;
  let paginationResizeTimer = 0;
  const inlineColorObservers = new WeakMap();

  const PAGINATION_SELECTOR =
    '[data-cd-jdgm-load-more="true"], .jdgm-paginate__next-page';
  const PAGINATION_LOADING_CLASS = "cd-jdgm-pagination-loading";
  const PAGINATION_OVERLAY_CLASS = "cd-jdgm-pagination-overlay";
  const PAGINATION_SNAPSHOT_CLASS = "cd-jdgm-pagination-snapshot";
  const PAGINATION_RESERVED_CLASS = "cd-jdgm-pagination-space-reserved";
  const PAGINATION_HEADER_HIDDEN_CLASS =
    "cd-jdgm-pagination-header-was-hidden";
  const PAGINATION_HEADER_VISIBLE_CLASS =
    "cd-jdgm-pagination-header-was-visible";
  const SCROLL_BLOCKING_KEYS = new Set([
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "End",
    "Home",
    "PageDown",
    "PageUp",
    " ",
  ]);

  const setPaginationOverlayBounds = () => {
    if (!paginationLoad) return;

    const widget =
      paginationLoad.widget?.isConnected
        ? paginationLoad.widget
        : document.querySelector(WIDGET_SELECTOR);

    if (!widget) return;

    paginationLoad.widget = widget;

    /* Judge.me temporarily empties the live widget while fetching the next
       page. Use the pre-click geometry, not the changing live DOM, so the
       loading layer cannot shrink along with it. */
    const rect = paginationLoad.snapshotRect || widget.getBoundingClientRect();
    const top = Math.max(0, Math.min(window.innerHeight, rect.top));
    const bottom = Math.max(top, Math.min(window.innerHeight, rect.bottom));
    const height = Math.max(1, bottom - top);

    /* Make the overlay itself match the visible part of the frozen review
       section. Keeping the snapshot inside this clipped coordinate space is
       important on tall/wide monitors, where Judge.me's temporary scroll can
       otherwise expose a second copy above the original section. */
    paginationLoad.overlay.style.setProperty(
      "--cd-jdgm-pagination-overlay-top",
      `${top}px`,
    );
    paginationLoad.overlay.style.setProperty(
      "--cd-jdgm-pagination-overlay-height",
      `${height}px`,
    );
    paginationLoad.overlay.style.setProperty(
      "--cd-jdgm-snapshot-top",
      `${rect.top - top}px`,
    );
  };

  const holdPaginationScroll = () => {
    if (!paginationLoad || paginationLoad.restoringScroll) return;

    if (
      window.scrollX === paginationLoad.scrollX &&
      window.scrollY === paginationLoad.scrollY
    ) {
      return;
    }

    paginationLoad.restoringScroll = true;
    window.scrollTo(paginationLoad.scrollX, paginationLoad.scrollY);
    paginationLoad.restoringScroll = false;
  };

  const guardPaginationScrollFrame = () => {
    if (!paginationLoad) return;

    holdPaginationScroll();
    paginationLoad.scrollRaf = window.requestAnimationFrame(
      guardPaginationScrollFrame,
    );
  };

  const blockPaginationScroll = (event) => {
    if (!paginationLoad) return;

    if (event.type === "keydown" && !SCROLL_BLOCKING_KEYS.has(event.key)) {
      return;
    }

    event.preventDefault();
  };

  const createPaginationSnapshot = (widget, overlay, rect) => {
    const snapshot = widget.cloneNode(true);

    snapshot.classList.add(PAGINATION_SNAPSHOT_CLASS);
    snapshot.setAttribute("data-cd-jdgm-pagination-snapshot", "true");
    snapshot.setAttribute("aria-hidden", "true");
    snapshot.setAttribute("inert", "");
    snapshot.removeAttribute("id");
    snapshot.querySelectorAll("[id]").forEach((node) => {
      node.removeAttribute("id");
    });

    overlay.style.setProperty("--cd-jdgm-snapshot-left", `${rect.left}px`);
    overlay.style.setProperty("--cd-jdgm-snapshot-top", "0px");
    overlay.style.setProperty("--cd-jdgm-snapshot-width", `${rect.width}px`);
    overlay.style.setProperty("--cd-jdgm-snapshot-height", `${rect.height}px`);
    overlay.appendChild(snapshot);

    return snapshot;
  };

  const applyStablePaginationHeight = (widget, height) => {
    if (!widget || !height) return;

    stablePaginationHeight = Math.max(stablePaginationHeight, Math.ceil(height));
    stablePaginationWidth = window.innerWidth;

    widget.style.setProperty(
      "--cd-jdgm-stable-pagination-height",
      `${stablePaginationHeight}px`,
    );
    widget.classList.add("cd-jdgm-height-stabilized");
  };

  const resetStablePaginationHeight = () => {
    stablePaginationHeight = 0;
    stablePaginationWidth = window.innerWidth;

    document.querySelectorAll(WIDGET_SELECTOR).forEach((widget) => {
      widget.classList.remove("cd-jdgm-height-stabilized");
      widget.style.removeProperty("--cd-jdgm-stable-pagination-height");
    });
  };

  const handlePaginationResize = () => {
    if (paginationLoad || !stablePaginationHeight) return;
    if (Math.abs(window.innerWidth - stablePaginationWidth) < 2) return;

    window.clearTimeout(paginationResizeTimer);
    paginationResizeTimer = window.setTimeout(resetStablePaginationHeight, 150);
  };

  const finishPaginationLoad = () => {
    if (!paginationLoad) return;

    const state = paginationLoad;
    const currentWidget = state.widget?.isConnected
      ? state.widget
      : document.querySelector(WIDGET_SELECTOR);

    /* Keep at least the pre-click height after the loading state is released.
       A shorter batch of reviews otherwise reduces the document height and can
       force the browser upward when the captured scroll position is no longer
       reachable. scrollHeight also accounts for a replacement batch that needs
       more room than the old one. */
    if (currentWidget) {
      applyStablePaginationHeight(
        currentWidget,
        Math.max(
          state.widgetHeight,
          currentWidget.scrollHeight,
          currentWidget.getBoundingClientRect().height,
        ),
      );
    }

    paginationLoad = null;

    window.clearTimeout(state.finishTimer);
    window.clearTimeout(state.safetyTimer);
    window.cancelAnimationFrame(state.scrollRaf);
    state.observer.disconnect();

    window.removeEventListener("scroll", holdPaginationScroll);
    window.removeEventListener("resize", setPaginationOverlayBounds);
    window.removeEventListener("wheel", blockPaginationScroll);
    window.removeEventListener("touchmove", blockPaginationScroll);
    document.removeEventListener("keydown", blockPaginationScroll);

    document.querySelectorAll(WIDGET_SELECTOR).forEach((widget) => {
      widget.removeAttribute("aria-busy");
    });

    window.scrollTo(state.scrollX, state.scrollY);
    document.documentElement.classList.toggle(
      "cd-hero-header-hidden",
      state.headerHadHiddenClass,
    );
    document.documentElement.classList.toggle(
      "cd-hero-header-visible",
      state.headerHadVisibleClass,
    );
    document.documentElement.classList.remove(PAGINATION_LOADING_CLASS);
    document.documentElement.classList.remove(
      PAGINATION_HEADER_HIDDEN_CLASS,
      PAGINATION_HEADER_VISIBLE_CLASS,
    );
    document.documentElement.style.removeProperty(
      "--cd-jdgm-pagination-height",
    );
    document.documentElement.style.removeProperty(
      "--cd-jdgm-pagination-overlay-top",
    );

    state.containers.forEach((container) => {
      container.classList.remove(PAGINATION_RESERVED_CLASS);
      container.style.removeProperty("--cd-jdgm-pagination-space-height");
    });
    state.overlay.remove();

    /* Keep the exact viewport through the two paint frames in which the
       loading-only max-height and overlay are removed. */
    window.requestAnimationFrame(() => {
      window.scrollTo(state.scrollX, state.scrollY);
      window.requestAnimationFrame(() => {
        window.scrollTo(state.scrollX, state.scrollY);
      });
    });
  };

  const queuePaginationFinish = () => {
    if (!paginationLoad) return;

    window.clearTimeout(paginationLoad.finishTimer);
    paginationLoad.finishTimer = window.setTimeout(finishPaginationLoad, 450);
  };

  const beginPaginationLoad = (widget) => {
    if (paginationLoad || !widget || !document.body) return;

    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    const widgetRect = widget.getBoundingClientRect();
    const widgetHeight = Math.max(widgetRect.height, stablePaginationHeight);
    const innerContainer =
      widget.closest(".jdgm-review-widget") || widget.parentElement;
    const outerContainer =
      widget.closest(".shopify-app-block") || innerContainer;
    const containers = [...new Set([innerContainer, outerContainer])].filter(
      Boolean,
    );
    const header = document.querySelector(".t4s-section-header");
    const headerHadHiddenClass = document.documentElement.classList.contains(
      "cd-hero-header-hidden",
    );
    const headerHadVisibleClass = document.documentElement.classList.contains(
      "cd-hero-header-visible",
    );
    const headerWasHidden =
      headerHadHiddenClass ||
      Boolean(header && header.getBoundingClientRect().bottom <= 0);
    const overlay = document.createElement("div");

    overlay.className = PAGINATION_OVERLAY_CLASS;
    overlay.setAttribute("aria-hidden", "true");
    createPaginationSnapshot(widget, overlay, widgetRect);
    document.body.appendChild(overlay);

    const state = {
      scrollY,
      scrollX,
      widget,
      widgetHeight,
      containers,
      snapshotRect: widgetRect,
      overlay,
      finishTimer: 0,
      safetyTimer: 0,
      scrollRaf: 0,
      restoringScroll: false,
      headerHadHiddenClass,
      headerHadVisibleClass,
      observer: null,
    };

    state.observer = new MutationObserver((mutations) => {
      const reviewsChanged = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) => {
          if (!(node instanceof Element)) return false;

          return (
            node.matches?.(".jdgm-rev, .jdgm-rev-widg__reviews, .jdgm-rev-widg") ||
            Boolean(
              node.querySelector?.(
                ".jdgm-rev, .jdgm-rev-widg__reviews, .jdgm-rev-widg",
              ),
            )
          );
        }),
      );

      if (reviewsChanged) queuePaginationFinish();
    });

    paginationLoad = state;
    document.documentElement.style.setProperty(
      "--cd-jdgm-pagination-height",
      `${widgetHeight}px`,
    );
    containers.forEach((container) => {
      const containerHeight = Math.max(
        container.getBoundingClientRect().height || 0,
        widgetHeight,
      );

      container.style.setProperty(
        "--cd-jdgm-pagination-space-height",
        `${containerHeight}px`,
      );
      container.classList.add(PAGINATION_RESERVED_CLASS);
    });
    setPaginationOverlayBounds();
    document.documentElement.classList.add(PAGINATION_LOADING_CLASS);
    document.documentElement.classList.add(
      headerWasHidden
        ? PAGINATION_HEADER_HIDDEN_CLASS
        : PAGINATION_HEADER_VISIBLE_CLASS,
    );
    widget.setAttribute("aria-busy", "true");

    window.addEventListener("scroll", holdPaginationScroll, { passive: true });
    window.addEventListener("resize", setPaginationOverlayBounds, {
      passive: true,
    });
    window.addEventListener("wheel", blockPaginationScroll, { passive: false });
    window.addEventListener("touchmove", blockPaginationScroll, {
      passive: false,
    });
    document.addEventListener("keydown", blockPaginationScroll, {
      passive: false,
    });
    state.scrollRaf = window.requestAnimationFrame(guardPaginationScrollFrame);

    state.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    state.safetyTimer = window.setTimeout(finishPaginationLoad, 20000);
  };

  const toNumber = (value) => {
    const match = String(value || "").match(/[0-9]+(?:\.[0-9]+)?/);
    return match ? Number(match[0]) : null;
  };

  const formatRating = (value) => {
    const rating = toNumber(value);
    if (rating === null || Number.isNaN(rating)) return "";
    return rating.toFixed(2);
  };

  const formatCount = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/[0-9,]+/);
    return match ? match[0] : raw;
  };

  const parseDate = (raw) => {
    if (!raw) return "";

    const text = String(raw).trim();
    const dateFromData = text.includes("UTC")
      ? new Date(text.replace(" UTC", "Z"))
      : new Date(text);

    if (!Number.isNaN(dateFromData.getTime())) {
      return dateFromData.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    const numeric = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    if (numeric) {
      const [, month, day, year] = numeric;

      return new Date(
        Date.UTC(Number(year), Number(month) - 1, Number(day)),
      ).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    return text;
  };

  const setText = (node, value) => {
    if (!node) return;

    const next = String(value || "");

    if (node.textContent !== next) {
      node.textContent = next;
    }
  };

  const setAttribute = (node, name, value) => {
    if (!node) return;

    const next = String(value);

    if (node.getAttribute(name) !== next) {
      node.setAttribute(name, next);
    }
  };

  const getOriginalText = (node, key) => {
    if (!node) return "";

    const attr = `data-cd-jdgm-original-${key}`;
    const saved = node.getAttribute(attr);

    if (saved !== null) return saved;

    const current = node.textContent || "";
    node.setAttribute(attr, current);

    return current;
  };

  const cleanInlineColorNode = (node) => {
    if (!(node instanceof HTMLElement)) return false;

    const before = node.getAttribute("style");

    if (!before || !/color\s*:/i.test(before)) return false;

    const keptDeclarations = before
      .split(";")
      .map((declaration) => declaration.trim())
      .filter((declaration) => {
        if (!declaration) return false;

        const propName = declaration.split(":")[0];

        if (!propName) return false;

        return propName.trim().toLowerCase() !== "color";
      });

    if (keptDeclarations.length) {
      node.setAttribute("style", `${keptDeclarations.join("; ")};`);
    } else {
      node.removeAttribute("style");
    }

    return before !== node.getAttribute("style");
  };

  const removeInlineTextColors = (root) => {
    if (!root) return;

    const scope = root.closest?.(JUDGEME_SCOPE_SELECTOR) || root;

    cleanInlineColorNode(scope);

    scope.querySelectorAll("[style]").forEach((node) => {
      cleanInlineColorNode(node);
    });
  };

  const watchInlineTextColors = (widget) => {
    if (!widget) return;

    const scope = widget.closest(JUDGEME_SCOPE_SELECTOR) || widget;

    if (!scope || inlineColorObservers.has(scope)) return;

    removeInlineTextColors(scope);

    const colorObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          cleanInlineColorNode(mutation.target);
          return;
        }

        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            cleanInlineColorNode(node);

            node.querySelectorAll?.("[style]").forEach((child) => {
              cleanInlineColorNode(child);
            });
          });
        }
      });
    });

    colorObserver.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    inlineColorObservers.set(scope, colorObserver);
  };

  const enhanceSummary = (widget) => {
    const summaryInner = widget.querySelector(".jdgm-rev-widg__summary-inner");
    const average = widget.querySelector(".jdgm-rev-widg__summary-average");
    const summaryText = widget.querySelector(".jdgm-rev-widg__summary-text");

    if (summaryInner && average) {
      average.classList.add("cd-jdgm-summary-number");

      const originalAverage = getOriginalText(average, "average");
      const rating = formatRating(
        widget.getAttribute("data-average-rating") ||
          originalAverage ||
          average.textContent,
      );

      if (rating) {
        setText(average, rating);
      }

      if (summaryText && average.nextElementSibling !== summaryText) {
        summaryInner.insertBefore(average, summaryText);
      } else if (!summaryText && average.parentElement !== summaryInner) {
        summaryInner.insertBefore(average, summaryInner.firstChild);
      }
    }

    if (summaryText) {
      const originalSummary = getOriginalText(summaryText, "summary");
      const count = formatCount(
        widget.getAttribute("data-number-of-reviews") ||
          originalSummary ||
          summaryText.textContent,
      );

      if (count) {
        setText(summaryText, `Based on ${count} reviews`);
      }
    }
  };

  const enhanceHistogram = (widget) => {
    widget
      .querySelectorAll(".jdgm-histogram__frequency")
      .forEach((frequency) => {
        const originalFrequency = getOriginalText(frequency, "frequency");
        const number = formatCount(originalFrequency || frequency.textContent);

        if (number) {
          setText(frequency, `(${number})`);
        }
      });

    widget.querySelectorAll(".jdgm-histogram__row").forEach((row) => {
      const bar = row.querySelector(".jdgm-histogram__bar-content");
      const percentage = row.getAttribute("data-percentage");

      if (bar && percentage && bar.style.width !== `${percentage}%`) {
        bar.style.width = `${percentage}%`;
      }
    });
  };

  const enhanceReview = (review) => {
    const title = review.querySelector(".jdgm-rev__title");
    const body = review.querySelector(".jdgm-rev__body");
    const content = review.querySelector(".jdgm-rev__content");
    const author = review.querySelector(".jdgm-rev__author");
    const timestamp = review.querySelector(".jdgm-rev__timestamp");

    review.classList.add("cd-jdgm-enhanced");

    if (title && !title.textContent.trim()) {
      review.classList.add("cd-jdgm-empty-title");
    } else {
      review.classList.remove("cd-jdgm-empty-title");
    }

    if (!content) return;

    const authorText = author ? author.textContent.trim() : "";
    const dateText = timestamp
      ? parseDate(
          timestamp.getAttribute("data-content") || timestamp.textContent,
        )
      : "";
    const metaText = [authorText, dateText].filter(Boolean).join(" · ");

    const existingMetas = Array.from(
      content.querySelectorAll(".cd-jdgm-review-meta"),
    );

    let meta = existingMetas[0];

    if (!meta) {
      meta = document.createElement("div");
      meta.className = "cd-jdgm-review-meta";
      content.appendChild(meta);
    }

    existingMetas.slice(1).forEach((duplicateMeta) => {
      duplicateMeta.remove();
    });

    if (body && meta.previousElementSibling !== body) {
      body.insertAdjacentElement("afterend", meta);
    }

    setText(meta, metaText);
  };

  const enhancePagination = (widget) => {
    widget.querySelectorAll(".jdgm-paginate__next-page").forEach((link) => {
      setText(link, "Load more");
      setAttribute(link, "title", "Load more reviews");
      setAttribute(link, "data-cd-jdgm-load-more", "true");
    });
  };

  const enhanceWidget = (widget) => {
    if (!widget) return;

    widget.classList.add("cd-jdgm-figma-ready");

    if (stablePaginationHeight) {
      applyStablePaginationHeight(widget, stablePaginationHeight);
    }

    if (paginationLoad) {
      widget.setAttribute("aria-busy", "true");
    }

    watchInlineTextColors(widget);
    removeInlineTextColors(widget);

    enhanceSummary(widget);
    enhanceHistogram(widget);
    widget.querySelectorAll(".jdgm-rev").forEach(enhanceReview);
    enhancePagination(widget);

    removeInlineTextColors(widget);
  };

  const startDomObserver = () => {
    if (domObserver || !document.documentElement) return;

    domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;

        const changedInsideWidget = Array.from(mutation.addedNodes).some(
          (node) => {
            if (!(node instanceof Element)) return false;

            return (
              node.matches?.(WIDGET_SELECTOR) ||
              node.closest?.(WIDGET_SELECTOR) ||
              node.querySelector?.(WIDGET_SELECTOR)
            );
          },
        );

        if (changedInsideWidget) {
          schedule();
          return;
        }
      }
    });

    domObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const stopDomObserver = () => {
    if (!domObserver) return;

    domObserver.disconnect();
    domObserver = null;
  };

  const run = () => {
    pending = false;

    stopDomObserver();

    try {
      document.querySelectorAll(WIDGET_SELECTOR).forEach(enhanceWidget);
    } finally {
      startDomObserver();
    }
  };

  const schedule = () => {
    if (pending) return;

    pending = true;
    window.requestAnimationFrame(run);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule, { once: true });
  } else {
    schedule();
  }

  window.addEventListener("load", schedule, { once: true });
  window.addEventListener("resize", handlePaginationResize, { passive: true });
  document.addEventListener("jdgm.loaded", schedule);
  document.addEventListener("jdgm.reviewsLoaded", () => {
    schedule();
    queuePaginationFinish();
  });

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest?.(PAGINATION_SELECTOR);

      if (!trigger) return;

      if (paginationLoad) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      beginPaginationLoad(trigger.closest(WIDGET_SELECTOR));
    },
    true,
  );

  window.addEventListener("pagehide", finishPaginationLoad);

  startDomObserver();
})();
